import { PlannerAction, PlannerActionTypes } from "../types";

export type PlannerState = {
    itemsById:                      { [id: number]: Spintr.PlannerItem };
    allItemIds:                     number[];

    categoriesById:                 { [id: string]: Spintr.PlannerItemCategory };
    allCategoryIds:                 string[];
    categoriesLoading:              boolean;

    boardsById:                     { [id: number]: Spintr.PlannerBoard };
    allBoardIds:                    number[];
    
    labelsById:                     { [id: string]: Spintr.PlannerLabel };
    allLabelIds:                    string[];
    
    queryItemIds:                   number[];
    queryPending:                   boolean;
    queryResultCount:               number;
    
    initialItemsFetchDone:          boolean;
    initialBoardsFetchDone:         boolean;
};

const initialState: PlannerState = {
    itemsById:                      {},
    allItemIds:                     [],

    categoriesById:                 {},
    allCategoryIds:                 [],
    categoriesLoading:              false,

    boardsById:                     {},
    allBoardIds:                    [],

    labelsById:                     {},
    allLabelIds:                    [],

    queryItemIds:                   [],
    queryPending:                   false,
    queryResultCount:               0,

    initialItemsFetchDone:          false,
    initialBoardsFetchDone:         false,
};

export function plannerReducer(state: PlannerState = initialState, action: PlannerAction): PlannerState {
    switch (action.type) {
        case PlannerActionTypes.PlannerItemCreated:
            return {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.item.id]: action.item,
                },
                allItemIds: state.allItemIds.every(id => id !== action.item.id)
                    ? [...state.allItemIds, action.item.id]
                    : state.allItemIds,

                allCategoryIds: state.allCategoryIds.every(id => id !== action.item.category.id)
                    ? [...state.allCategoryIds, action.item.category.id]
                    : state.allCategoryIds,

                categoriesById: !!state.categoriesById[action.item.category.id] ? state.categoriesById : {
                    ...state.categoriesById,
                    [action.item.category.id]: action.item.category,
                },
            };

        case PlannerActionTypes.PlannerItemDeleted:
            return {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.id]: undefined,
                },
                allItemIds: state.allItemIds.filter(id => id !== action.id),
                queryItemIds: state.queryItemIds.filter(id => id !== action.id),
            };

        case PlannerActionTypes.PlannerItemUpdated:
            return {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.item.id]: action.item,
                },
                allItemIds: state.allItemIds.every(id => id !== action.item.id)
                    ? [...state.allItemIds, action.item.id]
                    : state.allItemIds,

                allCategoryIds: state.allCategoryIds.every(id => id !== action.item.category.id)
                    ? [...state.allCategoryIds, action.item.category.id]
                    : state.allCategoryIds,
                categoriesById: !!state.categoriesById[action.item.category.id] ? state.categoriesById : {
                    ...state.categoriesById,
                    [action.item.category.id]: action.item.category,
                },
            };

        case PlannerActionTypes.PlannerFileAdded: {
            const fileItem = state.itemsById[action.file.itemId];
            if (!fileItem) {
                return state;
            }

            const existingFiles = fileItem.files ?? [];
            if (existingFiles.some((file) => file.id === action.file.id)) {
                return state;
            }

            return {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.file.itemId]: {
                        ...fileItem,
                        files: [...existingFiles, action.file],
                    },
                },
            };
        }

        case PlannerActionTypes.PlannerFileRemoved:
            return !state.itemsById[action.itemId] ? state : {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.itemId]: {
                        ...state.itemsById[action.itemId],
                        files: (state.itemsById[action.itemId].files ?? []).filter(
                            (file) => file.id !== action.fileId,
                        ),
                    },
                },
            };

        case PlannerActionTypes.FindPlannerItemFulfilled:
            return {
                ...state,
                itemsById: {
                    ...state.itemsById,
                    [action.payload.id]: action.payload,
                },
                allItemIds: state.allItemIds.every(id => id !== action.payload.id)
                    ? [...state.allItemIds, action.payload.id]
                    : state.allItemIds,

                allCategoryIds: state.allCategoryIds.every(id => id !== action.payload.category.id)
                    ? [...state.allCategoryIds, action.payload.category.id]
                    : state.allCategoryIds,
                categoriesById: !!state.categoriesById[action.payload.category.id] ? state.categoriesById : {
                    ...state.categoriesById,
                    [action.payload.category.id]: action.payload.category,
                },
            };

        case PlannerActionTypes.PlannerCategoryQueryPending:
            return {
                ...state,
                categoriesLoading: true,
            };

        case PlannerActionTypes.PlannerCategoryQueryRejected:
            return {
                ...state,
                categoriesLoading: false,
            };

        case PlannerActionTypes.PlannerCategoryQueryFulfilled:
            return {
                ...state,
                categoriesLoading: false,
                allCategoryIds: action.payload.items.map((item) => item.id),
                categoriesById: action.payload.items.reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, { ...state.categoriesById }),
            };

        case PlannerActionTypes.PlannerItemQueryPending:
            return {
                ...state,
                queryPending: true,
            };

        case PlannerActionTypes.PlannerItemQueryRejected:
            return {
                ...state,
                queryPending: false,
            };

        case PlannerActionTypes.PlannerItemQueryFulfilled:
            const updatedItems = action.payload.items.reduce((acc, item) => {
                acc[item.id] = item;
                return acc;
            }, { ...state.itemsById});

            return {
                ...state,
                itemsById: updatedItems,
                allItemIds: Object.keys(updatedItems).map((id) => parseInt(id, 10)),
                queryItemIds: action.payload.items.map((item) => item.id),
                queryPending: false,
                queryResultCount: action.payload.totalCount,
                allCategoryIds: action.payload.items.reduce((acc, item) => {
                    if (acc.every(id => id !== item.category.id)) {
                        acc.push(item.category.id);
                    }
                    return acc;
                }, [...state.allCategoryIds]),
                categoriesById: action.payload.items.reduce((acc, item) => {
                    const categoryId = item.category.id;

                    if (!acc[categoryId]) {
                        acc[categoryId] = item.category;
                    }

                    return acc;
                }, { ...state.categoriesById }),
                initialItemsFetchDone: true,
            }

        case PlannerActionTypes.PlannerBoardQueryFulfilled:
            return {
                ...state,
                boardsById: Object.fromEntries(action.payload.map((item, index) => [item.id, {...item, index}])),
                allBoardIds: action.payload.map((b) => b.id),
                initialBoardsFetchDone: true,
            }

        case PlannerActionTypes.PlannerLabelQueryFulfilled:
            return {
                ...state,
                labelsById: Object.fromEntries(action.payload.map(item => [item.id, {
                    ...item,
                    date: item.date,
                    formattedDate: new Date(item.date),
                }])),
                allLabelIds: action.payload.map((b) => b.id),
            }
            
        case PlannerActionTypes.PlannerBoardCreated:
            return {
                ...state,
                boardsById: {
                    ...state.boardsById,
                    [action.item.id]: action.item,
                },
                allBoardIds: state.allBoardIds.every(id => id !== action.item.id)
                    ? [...state.allBoardIds, action.item.id]
                    : state.allBoardIds,
            };

        case PlannerActionTypes.PlannerBoardDeleted:
            return {
                ...state,
                boardsById: Object.fromEntries(Object.entries({...state.boardsById}).filter(([key]) => key !== action.id.toString())),
                allBoardIds: state.allBoardIds.filter(id => id !== action.id),
            };

        case PlannerActionTypes.PlannerBoardUpdated:
            const x = {
                ...state,
                boardsById: {
                    ...state.boardsById,
                    [action.item.id]: action.item,
                },
                allBoardIds: state.allBoardIds.every(id => id !== action.item.id)
                    ? [...state.allBoardIds, action.item.id]
                    : state.allBoardIds,
            };

            return x;

        case PlannerActionTypes.ToggleBoardIsHidden:
            return {
                ...state,
                boardsById: {
                    ...state.boardsById,
                    [action.boardId]: {
                        ...state.boardsById[action.boardId],
                        isHidden: !state.boardsById[action.boardId].isHidden,
                    },
                },
            }

        case PlannerActionTypes.AppendPlannerItems:
            return {
                ...state,
                itemsById: action.items.reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, { ...state.itemsById }),
                allItemIds: [
                    ...state.allItemIds,
                    ...action.items.filter(item => !state.itemsById[item.id]).map(item => item.id)
                ],
                allCategoryIds: action.items.reduce((acc, item) => {
                    if (acc.every(id => id !== item.category.id)) {
                        acc.push(item.category.id);
                    }
                    return acc;
                }, [...state.allCategoryIds]),
                categoriesById: action.items.reduce((acc, item) => {
                    if (!acc[item.category.id]) {
                        acc[item.category.id] = item.category;
                    }
                    return acc;
                }, { ...state.categoriesById }),
            };

        default:
            return state;
    }
}
